import {
    WorkspaceDescriptor,
    WorkspaceWithLinkDescriptor,
    WorkspaceType,
    LinkDescriptor,
} from '@synapex/shared2/entities'
import { KindaPlain, WithOptional } from '@synapex/tools/typescript'
import { validateSync } from 'class-validator'
import { plainToInstance, instanceToPlain } from 'class-transformer'

export type OmitWwld<T extends object> = Omit<
    T,
    keyof WorkspaceWithLinkDescriptor
>

export const pickDto = <C extends new (...args: unknown[]) => object>(
    cls: C,
    plain: InstanceType<C>
): KindaPlain<InstanceType<C>> => {
    //pick only decorated props
    const inst = plainToInstance(cls, plain) as InstanceType<C>
    const errors = validateSync(inst, { whitelist: true })
    if (errors.length > 0) {
        console.error(errors)
    }
    //@ts-expect-error generic
    return instanceToPlain(inst)
}

export function workspaceQueryKey<Name extends string>(name: Name): [name: Name]
export function workspaceQueryKey<Name extends string>(
    name: Name,
    descriptor: WorkspaceDescriptor
): [name: Name, workspace_type: WorkspaceType, workspace_id: number]
export function workspaceQueryKey<Name extends string>(
    name: Name,
    descriptor?: WorkspaceDescriptor
):
    | [name: Name]
    | [name: Name, workspace_type: WorkspaceType, workspace_id: number]
export function workspaceQueryKey<
    Name extends string,
    Extras extends Array<number | string | object>,
>(
    name: Name,
    descriptor: WorkspaceDescriptor,
    extras: Extras
): [name: Name, workspace_type: WorkspaceType, workspace_id: number, ...Extras]
export function workspaceQueryKey<Name extends string>(
    name: Name,
    descriptor?: WorkspaceDescriptor,
    extras: (number | string | object)[] = []
):
    | [name: Name]
    | [
          name: Name,
          workspace_type: WorkspaceType,
          workspace_id: number,
          ...(number | string | object)[],
      ] {
    if (descriptor) {
        const { workspace_type, workspace_id } = descriptor
        return [name, workspace_type, workspace_id, ...extras]
    } else {
        return [name]
    }
}

export function linkQueryKey<Name extends string>(name: Name): [name: Name]
export function linkQueryKey<Name extends string>(
    name: Name,
    descriptor: WorkspaceDescriptor
): [name: Name, workspace_type: WorkspaceType, workspace_id: number]
export function linkQueryKey<Name extends string>(
    name: Name,
    descriptor?: WorkspaceDescriptor
):
    | [name: Name]
    | [name: Name, workspace_type: WorkspaceType, workspace_id: number]
export function linkQueryKey<Name extends string>(
    name: Name,
    descriptor?: WorkspaceWithLinkDescriptor
):
    | [name: Name]
    | [
          name: Name,
          workspace_type: WorkspaceType,
          workspace_id: number,
          link_id: number,
      ]
export function linkQueryKey<Name extends string>(
    name: Name,
    descriptor: WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>
):
    | [name: Name, workspace_type: WorkspaceType, workspace_id: number]
    | [
          name: Name,
          workspace_type: WorkspaceType,
          workspace_id: number,
          link_id: number,
      ]
export function linkQueryKey<Name extends string>(
    name: Name,
    descriptor?: WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>
):
    | [name: Name]
    | [name: Name, workspace_type: WorkspaceType, workspace_id: number]
    | [
          name: Name,
          workspace_type: WorkspaceType,
          workspace_id: number,
          link_id: number,
      ]
export function linkQueryKey<
    Name extends string,
    Extras extends Array<number | string | object>,
>(
    name: Name,
    descriptor: WorkspaceWithLinkDescriptor,
    extras: Extras
): [
    name: Name,
    workspace_type: WorkspaceType,
    workspace_id: number,
    link_id: number,
    ...Extras,
]
export function linkQueryKey<Name extends string>(
    name: Name,
    descriptor?: WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>,
    extras: (number | string)[] = []
):
    | [name: Name]
    | [name: Name, workspace_type: WorkspaceType, workspace_id: number]
    | [
          name: Name,
          workspace_type: WorkspaceType,
          workspace_id: number,
          link_id: number,
          ...(number | string)[],
      ] {
    if (descriptor) {
        const { workspace_type, workspace_id, link_id } = descriptor
        if (link_id) {
            return [name, workspace_type, workspace_id, link_id, ...extras]
        } else {
            return [name, workspace_type, workspace_id]
        }
    } else {
        return [name]
    }
}

export const linkPath = ({ workspace_type, link_id }: LinkDescriptor) =>
    `${workspace_type}/${link_id}`
